import MedicineTranslationActivity from "./MedicineTranslationActivity";
import useTranslationModels from "../../../hooks/useTranslationModels";
import TranslationModelSelect from "./TranslationModelSelect";
import LanguageLabel from "./LanguageLabel";
import React, { useEffect, useId, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
  TextField,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { axiosInstance } from "../../../config";
export const translationLanguages = {
  cn: "Chinese",
  ru: "Russian",
  es: "Spanish",
  sa: "Arabic",
  pt: "Portuguese",
  all: "All languages",
};
const fieldLabels = {
  pageTitle: "Page title",
  metaTitle: "Meta title",
  metaDesc: "Meta description",
  metaKeyword: "Meta keywords",
};
export function TranslationFieldButton({ field, label, onSave, disabled }) {
  const models = useTranslationModels();
  const [anchor, setAnchor] = useState(null);
  const name =
    label || fieldLabels[field] || field.replace("section-name:", "");
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(220px, 1fr) auto" },
        alignItems: "start",
        gap: 1.5,
        mb: 1,
        p: 1.5,
        bgcolor: "action.hover",
        borderRadius: 1,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, gridColumn: "1 / -1" }}>
        {name} translation
      </Typography>
      <TranslationModelSelect models={models} disabled={disabled} label={`Model for ${name}`} sx={{width:"100%"}} />
      <Button
        size="small"
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
        disabled={disabled || !models.ready}
        aria-label={`Save and translate ${name}`}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{ minHeight: 40, whiteSpace: "nowrap" }}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        Save & translate field
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {Object.entries(translationLanguages).map(([language, title]) => (
          <MenuItem
            key={language}
            onClick={() => {
              setAnchor(null);
              onSave({ language, scope: "field", field, selection: models.selection });
            }}
          >
            <LanguageLabel code={language}>{title}</LanguageLabel>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
export function TranslationFormActions({ onSave, disabled, sourceId }) {
  const models = useTranslationModels();
  const [language, setLanguage] = useState("cn");
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem("medicine.translation.expanded.v1") === "true"; }
    catch { return false; }
  });
  const contentId = useId();
  const toggleExpanded = () => setExpanded(previous => {
    const next = !previous;
    try { localStorage.setItem("medicine.translation.expanded.v1", String(next)); } catch {}
    return next;
  });
  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        mb: 2,
        bgcolor: "background.paper",
      }}
    >
      <Button
        type="button"
        fullWidth
        aria-label="Toggle medicine translation tools"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={toggleExpanded}
        endIcon={<ExpandMoreIcon sx={{transform: expanded ? "rotate(180deg)" : "none", transition: "transform 150ms"}} />}
        sx={{justifyContent:"space-between",textAlign:"left",color:"text.primary",px:0,py:.5}}
      >
        <Typography component="span" variant="subtitle1" fontWeight={600}>Translate from English</Typography>
      </Button>
      {!expanded && <Typography variant="body2" color="text.secondary">Expand for translation models, language options and saved activity. Accepted jobs continue in the background.</Typography>}
      <Collapse in={expanded} timeout={150} unmountOnExit={false}>
      <Box id={contentId} sx={{pt:1.5}}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Save English changes and replace every translatable field in the selected language with a fresh translation, including clearing fields left empty in English. All
        languages run one at a time in the background. Progress stays on the
        AI translations page after you close this page.
      </Typography>
      <Box sx={{display:"grid", gap:1.5, alignItems:"start", gridTemplateColumns:{xs:"minmax(0, 1fr)",sm:"minmax(220px, 1fr) minmax(160px, .7fr)",lg:"minmax(260px, 1.2fr) minmax(160px, .65fr) auto"}}}>
        <TranslationModelSelect models={models} disabled={disabled} sx={{width:"100%"}} />
        <Select
          size="small"
          value={language}
          disabled={disabled}
          onChange={(e) => setLanguage(e.target.value)}
          inputProps={{ "aria-label": "Translation language" }}
          sx={{minWidth:0, minHeight:40}}
        >
          {Object.entries(translationLanguages).map(([value, title]) => (
            <MenuItem key={value} value={value}>
              <LanguageLabel code={value}>{title}</LanguageLabel>
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="contained"
          disabled={disabled || !models.ready}
          onClick={() => onSave({ language, scope: "medicine", selection: models.selection })}
          sx={{minHeight:40, whiteSpace:"nowrap", gridColumn:{xs:"auto",sm:"1 / -1",lg:"auto"}}}
        >
          Save & translate medicine
        </Button>
      </Box>
      {sourceId && <TranslationActivity sourceId={sourceId} compact polling={expanded} />}
      </Box>
      </Collapse>
    </Box>
  );
}
export function TranslationBudgetControl({ budget, onSaved }) {
  const [limit, setLimit] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [success, setSuccess] = useState("");
  useEffect(() => {
    setLimit(Number(budget.allocated.usd).toFixed(2));
  }, [budget.limitNano, budget.allocated.usd]);
  async function save() {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await axiosInstance.post("/api/translations/budget", {
        limitUsd: limit.trim(),
        expectedLimitNano: budget.limitNano,
      });
      const response = await axiosInstance.get("/api/translations/status");
      onSaved(response.data);
      setSuccess(
        "Limit saved. Budget-paused translations resume from the next unfinished medicine; completed medicines are retained.",
      );
    } catch (e) {
      setError(
        e.response?.data?.message ||
          "Could not save the limit. Refresh and try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Typography fontWeight={600}>Translation spending limit</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Set the total allowed spend, including money already spent. Saving
        resumes budget-paused groups. This limit applies to all queued
        translations and does not add credit to any provider account.
      </Typography>
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        <TextField
          label="Total spending limit (USD)"
          size="small"
          value={limit}
          disabled={busy}
          inputProps={{ inputMode: "decimal" }}
          onChange={(e) => {
            setLimit(e.target.value);
            setSuccess("");
          }}
        />
        <Button
          variant="contained"
          disabled={busy || !limit.trim()}
          onClick={save}
        >
          Save limit & resume
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {success}
        </Alert>
      )}
    </Box>
  );
}
export function TranslationActivity(props) {
  return <MedicineTranslationActivity {...props}/>;
}
